import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Camera, CameraOff, Check, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const READER_ID = "vangard-qr-reader";

export default function AdminScanPage() {
  const navigate = useNavigate();
  const { user, initialized } = useAuth();
  const { t } = useTranslation();
  const [scanning, setScanning] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [code, setCode] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    if (initialized && (!user || !user.is_admin)) navigate("/account", { replace: true });
  }, [initialized, user, navigate]);

  const verify = async (raw) => {
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/admin/verify/${encodeURIComponent(raw)}`);
      setResult(data);
      if (navigator.vibrate) navigator.vibrate(data.admitted ? 40 : [40, 60, 40]);
    } catch {
      toast.error(t("vc.scan.fail"));
    } finally {
      setChecking(false);
    }
  };

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (s) {
      try { await s.stop(); } catch { /* already stopped */ }
      try { s.clear(); } catch { /* ignore */ }
    }
  };

  const start = async () => {
    setResult(null);
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode(READER_ID, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        async (text) => { await stop(); verify(text); },
        () => {},
      );
    } catch {
      toast.error(t("vc.scan.camera-fail"));
      await stop();
    }
  };

  useEffect(() => () => { stop(); }, []);

  if (!initialized || !user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-neutral-500" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white marble-bg" data-testid="admin-scan-page">
      <Helmet>
        <title>{`${t("vc.scan.title")} · VangardClub`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/85 border-b border-white/[0.06]">
        <div className="max-w-[640px] mx-auto px-5 h-[54px] flex items-center justify-between">
          <Link to="/account" className="inline-flex items-center gap-2 -ml-2 px-2 py-2 text-[10px] tracking-[0.2em] uppercase text-neutral-500 hover:text-white transition-colors" data-testid="scan-back">
            <ArrowLeft size={14} /> {t("vc.acct.nav.overview")}
          </Link>
          <LangSwitcher />
        </div>
      </header>

      <main className="max-w-[640px] mx-auto px-5 pt-6 pb-16">
        <h1 className="font-serif text-2xl">{t("vc.scan.title")}</h1>
        <p className="text-sm text-neutral-400 font-light mt-2 leading-relaxed">{t("vc.scan.sub")}</p>

        <div className="mt-5 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 overflow-hidden">
          <div id={READER_ID} className={`w-full ${scanning ? "block" : "hidden"}`} />
          {!scanning && (
            <div className="aspect-square flex flex-col items-center justify-center gap-4 text-neutral-600">
              <Camera size={26} strokeWidth={1.4} />
              <span className="text-[10px] tracking-[0.3em] uppercase">QR</span>
            </div>
          )}
        </div>

        <button
          onClick={scanning ? stop : start}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 rounded-full text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
          data-testid="scan-toggle"
        >
          {scanning ? <><CameraOff size={14} /> {t("vc.scan.stop")}</> : <><Camera size={14} /> {t("vc.scan.start")}</>}
        </button>

        <form
          onSubmit={(e) => { e.preventDefault(); if (code.trim()) verify(code.trim()); }}
          className="mt-8"
        >
          <label className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.scan.manual")}</label>
          <div className="mt-2.5 flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              placeholder="3134075"
              className="flex-1 min-w-0 bg-transparent border border-white/[0.12] rounded-full px-4 py-3 text-sm font-mono tracking-[0.15em] outline-none focus:border-white/45 transition-colors placeholder:text-neutral-700"
              data-testid="scan-manual-input"
            />
            <button
              type="submit"
              disabled={checking || !code.trim()}
              className="shrink-0 inline-flex items-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 disabled:opacity-40 transition-colors"
              data-testid="scan-manual-submit"
            >
              {checking ? <Loader2 size={13} className="animate-spin" /> : t("vc.scan.check")}
            </button>
          </div>
        </form>

        {result && !result.found && (
          <div className="mt-8 rounded-2xl border border-white/[0.12] bg-[#0A0A0A]/80 p-6 text-center" data-testid="scan-not-found">
            <X size={22} className="mx-auto text-neutral-400" />
            <div className="font-serif text-xl mt-3">{t("vc.scan.unknown")}</div>
            <p className="text-sm text-neutral-500 font-light mt-2">{t("vc.scan.unknown-body")}</p>
            <div className="font-mono text-sm tracking-[0.2em] text-neutral-400 mt-3">#{result.member_id}</div>
            <button
              onClick={() => { setResult(null); setCode(""); start(); }}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
              data-testid="scan-again-unknown"
            >
              {t("vc.scan.again")}
            </button>
          </div>
        )}

        {result && result.found && (
          <div className="mt-8" data-testid="scan-result">
            <div
              className={`rounded-2xl border p-6 ${result.admitted ? "border-white/40 bg-white text-black" : "border-white/[0.15] bg-[#0A0A0A]/80 text-white"}`}
            >
              <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase" data-testid="scan-verdict">
                {result.admitted ? <Check size={14} /> : <X size={14} />}
                {result.admitted ? t("vc.scan.admitted") : t("vc.scan.refused")}
              </div>
              <div className="flex items-center gap-4 mt-4">
                {result.picture ? (
                  <img src={result.picture} alt={result.name || ""} className="w-14 h-14 rounded-xl object-cover grayscale" />
                ) : (
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-serif text-lg ${result.admitted ? "bg-black/10" : "bg-white/[0.06]"}`}>
                    {(result.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-serif text-2xl leading-tight truncate" data-testid="scan-member-name">{result.name || "—"}</div>
                  {result.company && <div className={`text-sm font-light truncate ${result.admitted ? "text-black/60" : "text-neutral-400"}`}>{result.company}</div>}
                </div>
              </div>
              <div className={`mt-5 grid grid-cols-3 gap-3 border-t pt-4 ${result.admitted ? "border-black/10" : "border-white/[0.08]"}`}>
                <Facts label={t("vc.scan.tier")} value={result.membership_tier.toUpperCase()} dark={result.admitted} testid="scan-tier" />
                <Facts label={t("vc.scan.standing")} value={t(`vc.onb.status.${result.application_status}`)} dark={result.admitted} />
                <Facts
                  label={t("vc.scan.since")}
                  value={result.member_since ? new Date(result.member_since).getFullYear() : "—"}
                  dark={result.admitted}
                />
              </div>
              <div className={`mt-4 font-mono text-sm tracking-[0.2em] ${result.admitted ? "text-black/60" : "text-neutral-500"}`}>#{result.member_id}</div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => { setResult(null); setCode(""); start(); }}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="scan-again"
              >
                {t("vc.scan.again")}
              </button>
              {result.slug && (
                <a
                  href={`/u/${result.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                  data-testid="scan-profile-link"
                >
                  {t("vc.scan.profile")} <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Facts = ({ label, value, dark, testid }) => (
  <div data-testid={testid}>
    <div className={`text-[8px] tracking-[0.25em] uppercase ${dark ? "text-black/50" : "text-neutral-500"}`}>{label}</div>
    <div className="text-[13px] mt-1 truncate">{value}</div>
  </div>
);
